import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  simuladorService,
  type GrupoSimulador,
  type ConfiguracionSimulador,
  type SimulacionResultado,
} from "@/features/simulador/services/simulador.service";

export function useSimulador() {
  const navigate = useNavigate();
  const { i18n, t } = useTranslation("simulador");
  const [grupos, setGrupos] = useState<GrupoSimulador[]>([]);
  const [grupoIndex, setGrupoIndex] = useState(0);
  const [configuracionSeleccionada, setConfiguracionSeleccionada] =
    useState<ConfiguracionSimulador | null>(null);
  const [userHasSelectedConfig, setUserHasSelectedConfig] = useState(false);
  const [plazoSeleccionado, setPlazoSeleccionado] = useState<number | null>(
    null,
  );
  const [userHasSelectedPlazo, setUserHasSelectedPlazo] = useState(false);
  const [monto, setMonto] = useState("");
  const [montoPreset, setMontoPreset] = useState<number | null>(null);
  const [resultado, setResultado] = useState<SimulacionResultado | null>(null);
  const [calculando, setCalculando] = useState(false);
  const [loadingProductos, setLoadingProductos] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contactando, setContactando] = useState(false);
  const [contactarExito, setContactarExito] = useState(false); // bloquea el botón permanentemente en la sesión
  const [showExitoModal, setShowExitoModal] = useState(false); // controla solo la visibilidad del modal
  const [contactarError, setContactarError] = useState<string | null>(null);
  const [descargando, setDescargando] = useState(false);
  const [descargarError, setDescargarError] = useState<string | null>(null);
  const [showSolicitudActivaModal, setShowSolicitudActivaModal] =
    useState(false);
  const [solicitudActivaMensaje, setSolicitudActivaMensaje] = useState<
    string | null
  >(null);

  useEffect(() => {
    simuladorService
      .listarProductos()
      .then((data) => {
        setGrupos(data);
        const primerGrupo = data[0];
        if (primerGrupo) {
          const configMensual =
            primerGrupo.configuraciones.find((c) => c.periodicidad === 1) ??
            primerGrupo.configuraciones[0] ??
            null;
          if (configMensual) {
            setConfiguracionSeleccionada(configMensual);
            const primerPlazoUnico = getPrimerPlazoUnico(configMensual);
            setPlazoSeleccionado(primerPlazoUnico);
          }
        }
      })
      .catch(() => setError(t("errorLoad")))
      .finally(() => setLoadingProductos(false));
  }, []);

  function getPrimerPlazoUnico(config: ConfiguracionSimulador): number | null {
    const vistos = new Set<number>();
    for (const p of config.plazosDisponibles) {
      if (!vistos.has(p.plazo)) {
        return p.plazo;
      }
    }
    return null;
  }

  const grupoActual = grupos[grupoIndex] ?? null;

  const irAnterior = () => setGrupoIndex((i) => Math.max(0, i - 1));

  const irSiguiente = () =>
    setGrupoIndex((i) => Math.min(grupos.length - 1, i + 1));

  const seleccionarConfiguracion = (config: ConfiguracionSimulador) => {
    setUserHasSelectedConfig(true);
    setUserHasSelectedPlazo(false);
    setConfiguracionSeleccionada(config);
    const primerPlazo = getPrimerPlazoUnico(config);
    setPlazoSeleccionado(primerPlazo);
    setResultado(null);
    setMontoPreset(null);
  };

  const selectPlazo = (plazo: number) => {
    setUserHasSelectedPlazo(true);
    setPlazoSeleccionado(plazo);
    setResultado(null);
  };

  const setMontoPresetAndMonto = (m: number) => {
    setMontoPreset(m);
    setMonto(m.toString());
  };

  const setMontoManual = (value: string) => {
    setMontoPreset(null);
    setMonto(value);
  };

  const plazosUnicos = useMemo(() => {
    const plazos = configuracionSeleccionada?.plazosDisponibles ?? [];
    const vistos = new Set<number>();
    const resultado: typeof plazos = [];
    for (const p of plazos) {
      if (!vistos.has(p.plazo)) {
        vistos.add(p.plazo);
        resultado.push(p);
      }
    }
    return resultado;
  }, [configuracionSeleccionada]);

  const montoMin = useMemo(() => {
    const plazoRef = plazoSeleccionado ?? plazosUnicos[0]?.plazo ?? null;
    if (plazoRef === null) return 10000;
    const tiers = (configuracionSeleccionada?.plazosDisponibles ?? []).filter(
      (p) => p.plazo === plazoRef,
    );
    if (tiers.length === 0) return 10000;
    return Math.min(...tiers.map((t) => t.montoMinimo ?? 10000));
  }, [configuracionSeleccionada, plazoSeleccionado, plazosUnicos]);

  const montosPreset = useMemo(() => {
    const plazoRef = plazoSeleccionado ?? plazosUnicos[0]?.plazo ?? null;
    if (plazoRef === null) return [];
    const tiers = (configuracionSeleccionada?.plazosDisponibles ?? []).filter(
      (p) => p.plazo === plazoRef,
    );
    if (tiers.length === 0) return [];

    const anclas = Array.from(new Set(tiers.map((t) => t.montoMinimo ?? 0)))
      .filter((v) => v > 0)
      .sort((a, b) => a - b);
    const presets: number[] = [];

    for (let i = 0; i < anclas.length; i++) {
      presets.push(anclas[i]);
      if (i < anclas.length - 1) {
        const medio = Math.round((anclas[i] + anclas[i + 1]) / 2 / 5000) * 5000;
        if (medio !== anclas[i] && medio !== anclas[i + 1]) {
          presets.push(medio);
        }
      }
    }

    const ultima = anclas[anclas.length - 1];
    if (ultima !== undefined) {
      presets.push(ultima + 10000);
      presets.push(ultima + 35000);
    }

    return presets;
  }, [configuracionSeleccionada, plazoSeleccionado, plazosUnicos]);

  const calcular = async () => {
    if (!configuracionSeleccionada || !monto || !plazoSeleccionado) return;

    const montoNum = parseFloat(monto.replace(/,/g, "."));
    if (isNaN(montoNum) || montoNum <= 0) return;

    setCalculando(true);
    setError(null);

    try {
      const res = await simuladorService.simular(
        configuracionSeleccionada.productNumber,
        montoNum,
        plazoSeleccionado,
      );
      setResultado(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("errorCalc"));
    } finally {
      setCalculando(false);
    }
  };

  const closeExitoModal = () => setShowExitoModal(false);
  const closeSolicitudActivaModal = () => setShowSolicitudActivaModal(false);

  const handleContactar = async () => {
    if (!resultado || !configuracionSeleccionada || !plazoSeleccionado) return;

    setContactando(true);
    setContactarError(null);

    try {
      const { puedeContactar: puede, mensaje } =
        await simuladorService.puedeContactar(i18n.language);

      if (!puede) {
        setSolicitudActivaMensaje(mensaje);
        setShowSolicitudActivaModal(true);
        return;
      }

      await simuladorService.contactar(
        {
          productNumber: configuracionSeleccionada.productNumber,
          monto: parseFloat(monto),
          plazo: plazoSeleccionado,
          periodicidad: configuracionSeleccionada.periodicidad,
          tasaAnual: resultado.tasaAnual,
          bonoAnual: resultado.bonoAnual ?? undefined,
          rendimientoTotal: resultado.rendimientoTotal,
          valorFinal: resultado.valorFinal,
        },
        i18n.language,
      );
      setContactarExito(true);
      setShowExitoModal(true);
    } catch (e) {
      setContactarError(e instanceof Error ? e.message : t("errorSend"));
    } finally {
      setContactando(false);
    }
  };

  const handleDescargar = async () => {
    if (!resultado || !configuracionSeleccionada || !plazoSeleccionado) return;

    setDescargando(true);
    setDescargarError(null);

    try {
      const pdfFileName = i18n.language?.split('-')[0] === 'en'
        ? "simulation_synergy.pdf"
        : "simulacion_synergy.pdf";
      await simuladorService.descargarPdf(
        configuracionSeleccionada.productNumber,
        resultado.montoInvertido,
        resultado.plazo,
        i18n.language,
        pdfFileName,
      );
      navigate('/simulador/descarga-exito', { state: { pdfFileName } });
    } catch (e) {
      setDescargarError(
        e instanceof Error ? e.message : "Error al generar el PDF.",
      );
    } finally {
      setDescargando(false);
    }
  };

  const montoNum = parseFloat(monto);
  const puedeCalcular =
    userHasSelectedConfig &&
    !!configuracionSeleccionada &&
    userHasSelectedPlazo &&
    !!plazoSeleccionado &&
    !!monto &&
    !isNaN(montoNum) &&
    montoNum >= montoMin &&
    !calculando;

  return {
    grupos,
    grupoActual,
    grupoIndex,
    configuracionSeleccionada,
    userHasSelectedConfig,
    plazoSeleccionado,
    userHasSelectedPlazo,
    plazosUnicos,
    montoMin,
    montosPreset,
    montoPreset,
    setMontoPresetAndMonto,
    monto,
    setMonto,
    setMontoManual,
    resultado,
    calculando,
    loadingProductos,
    error,
    irAnterior,
    irSiguiente,
    seleccionarConfiguracion,
    selectPlazo,
    calcular,
    puedeCalcular,
    contactando,
    contactarExito,
    showExitoModal,
    contactarError,
    handleContactar,
    closeExitoModal,
    showSolicitudActivaModal,
    solicitudActivaMensaje,
    closeSolicitudActivaModal,
    descargando,
    descargarError,
    handleDescargar,
  };
}
